import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import {
  GetArticleQuery,
  GetArticleCountQuery,
  GetArticleCountQueryVariables,
  GetArticlesQuery,
  GetArticleQueryVariables,
  GetArticlesQueryVariables,
} from '../../__generated__/queries-web';
import { DEFAULT_VOID_APOLLO_RESULT } from '../constants';

const GET_ARTICLES = gql`
  query GetArticles(
    $first: UnsignedInt!
    $where: ArticleFilterInput
    $skip: UnsignedInt
    $orderBy: [ArticleOrderingInput!]
  ) {
    articles(first: $first, where: $where, skip: $skip, orderBy: $orderBy) {
      id
      title
      status
      body
      createdAt
      publishedAt
      editedAt
      brandKey
      format
      medias
      pushCount
      url
      live {
        postCount
      }
      categories(first: $first, orderBy: order_ASC) {
        order
        category {
          id
          title
        }
      }
      channels(first: 1) {
        order
        category {
          id
          title
        }
      }
      creator {
        name
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      lockedAt
      lockedBy {
        id
        name
        email
        avatarUrl
      }
      events(first: 10) {
        category {
          id
          title
        }
        order
      }
      histories(first: 100, orderBy: createdAt_ASC) {
        user {
          ... on OneUser {
            name
          }
        }
      }
      qualifiers(first: 100) {
        qualifier {
          id
          title
        }
      }
      authors(first: 1) {
        author {
          id
          name
          avatarUrl
        }
      }
    }
  }
`;

const GET_ARTICLE_COUNT = gql`
  query GetArticleCount($where: ArticleFilterInput) {
    articleCount(where: $where)
  }
`;

export const GET_ARTICLE_QUERY = gql`
  query GetArticle($where: ArticleUniqueFilterInput!) {
    article(where: $where) {
      id
      title
      status
      body
      createdAt
      publishedAt
      editedAt
      brandKey
      format
      medias
      pushCount
      url
      live {
        postCount
      }
      categories(first: 2, orderBy: order_ASC) {
        order
        category {
          id
          title
        }
      }
      channels(first: 1) {
        order
        category {
          id
          title
        }
      }
      creator {
        name
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      lockedAt
      lockedBy {
        id
        name
        email
        avatarUrl
      }
      secondaryTitle
      partnerTitle
      lead
      slug
      live {
        postCount
        endedAt
      }
      tags(first: 20, orderBy: order_ASC) {
        order
        tag {
          id
          relationId
          title
          type
        }
      }
      authors(first: 10) {
        order
        author {
          id
          name
          user {
            id
            name
            avatarUrl
          }
        }
      }
      source {
        id
        title
        status
      }
      events(first: 100) {
        order
        category {
          id
          title
        }
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      guides(first: 1) {
        order
        category {
          id
          title
        }
      }
      machineTags(first: 100) {
        order
        machineTag {
          id
          tag
        }
      }
      histories(first: 100, orderBy: createdAt_DESC) {
        id
        createdAt
        user {
          ... on OneUser {
            id
            name
            avatarUrl
          }
        }
        article {
          id
        }
      }
      platformId
    }
  }
`;

export const useGetArticles = (
  variables: GetArticlesQueryVariables,
  skip?: boolean,
) =>
  useQuery<GetArticlesQuery, GetArticlesQueryVariables>(GET_ARTICLES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip,
    variables,
  });

export const useArticlesGetter = () => {
  const client = useApolloClient();

  return (variables: GetArticlesQueryVariables, skip?: boolean) =>
    skip
      ? Promise.resolve(DEFAULT_VOID_APOLLO_RESULT)
      : client.query<GetArticlesQuery, GetArticlesQueryVariables>({
          query: GET_ARTICLES,
          variables,
          fetchPolicy: 'network-only',
        });
};

export const useArticleCountGetter = () => {
  const client = useApolloClient();

  return useCallback(
    (variables: GetArticleCountQueryVariables, skip?: boolean) =>
      skip
        ? Promise.resolve(DEFAULT_VOID_APOLLO_RESULT)
        : client.query<GetArticleCountQuery, GetArticleCountQueryVariables>({
            query: GET_ARTICLE_COUNT,
            variables,
            fetchPolicy: 'network-only',
          }),
    [client],
  );
};

export const useGetArticle = (id: string) =>
  useQuery<GetArticleQuery, GetArticleQueryVariables>(GET_ARTICLE_QUERY, {
    variables: { where: { id } },
    skip: !id,
  });

export const useArticleGetter = () => {
  const client = useApolloClient();

  return (id: string) =>
    client.query<GetArticleQuery, GetArticleQueryVariables>({
      query: GET_ARTICLE_QUERY,
      variables: { where: { id } },
    });
};
